<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  showErrorMsg,
  showInfoMsg,
  watch,
} from '#imports';
import { useService } from '@kaokei/use-vue-service';
import GeneralConfirm from '@core/components/Confirm/GeneralConfirm.vue';
import type { AudioRecorderType } from '../../audio-recorder';
import { useAudioRecorderState } from '../../composables/use-audio-recorder-state';
import { MessageService } from '../../message.service';
import { getAudioDuration, roundToOneDecimal } from '../../utils';
import RecordLoading from '../RecordLoading.vue';
import { AudioActionService } from './AudioAction.service';
import AudioMessage from './AudioMessage.vue';

const audioActionService = useService(AudioActionService);
const messageService = useService(MessageService);
const props = defineProps<{ audioRecorder?: AudioRecorderType }>();
const showPermissionModal = ref(false);
const audioInfo = reactive({
  duration: 0,
  blob: null as Blob | null,
  url: '',
});
// const handlePermissionError = (error:Error) =>{
//     let message = '无法访问麦克风。';
//     switch(error.name) {
//         case 'NotAllowedError':
//             message = '您拒绝了麦克风访问权限';
//             break;
//         case 'NotFoundError':
//             message = '未找到麦克风设备。请确保您的设备已连接麦克风。';
//             break;
//         case 'NotReadableError':
//             message = '麦克风设备被占用，无法访问。请关闭其他使用麦克风的程序。';
//             break;
//         default:
//             message = '发生未知错误，无法访问麦克风。';
//     }
//     showErrorMsg(message);
// }
// 使用自定义 hook 管理录音状态
const {
  step,
  secondsToStart,
  secondsToStop,
  startCountdownBeforeStart,
  handleStart,
  showStopCountdown,
  handleStop,
  isGrantingPermission,
  clean: cleanState,
} = useAudioRecorderState({
  audioRecorder: props.audioRecorder!,
  onRecordingSuccess: async (data, duration) => {
    const favoriteDuration = await getAudioDuration(data)
      .then((duration) => {
        console.log('计算得到的时长', duration);
        return duration;
      })
      .catch((err) => {
        console.log(err);
        return undefined;
      });
    const finalDuration = favoriteDuration || duration;
    if (finalDuration < 0.5) {
      showInfoMsg('语音录音时间过短');
      startCountdownBeforeStart();
      return;
    }
    audioInfo.duration = roundToOneDecimal(finalDuration);
    audioInfo.blob = data;
    audioInfo.url = window.URL.createObjectURL(data);
  },
  onError: (error) => {
    showPermissionModal.value = true;
    close();
    clean();
  },
});

function close() {
  audioActionService.visible = false;
}

function clean() {
  cleanState();
  props.audioRecorder?.stop();

  if (audioInfo.url) {
    window.URL.revokeObjectURL(audioInfo.url);
    audioInfo.url = '';
  }

  audioInfo.blob = null;
  audioInfo.duration = 0;
}

function sendAudio() {
  if (audioInfo.blob) {
    const file = new File([audioInfo.blob], 'audio.wav', {
      type: 'audio/wav',
    });
    messageService.sendAudioMsgByFile(file, audioInfo.duration);
  }
  close();
}
const recordingText = computed(() => {
  return showStopCountdown.value
    ? `${secondsToStop.value}s后，语音录制即将完成`
    : '请对准麦克风说话';
});
watch(
  () => audioActionService.visible,
  (visible) => {
    if (visible) {
      startCountdownBeforeStart();
    } else {
      clean();
    }
  },
);

onBeforeUnmount(() => {
  clean();
});
</script>

<template>
  <div
    v-show="audioActionService.visible"
    class="absolute left-0 top-0 flex h-full w-full flex-col justify-center bg-white p-5 pt-0"
  >
    <div class="flex-center flex-1">
      <div v-if="step === 'COUNTDOWN_TO_START'" class="flex-1 text-center">
        <template v-if="isGrantingPermission"
          ><p class="text-colorTextSecondary">麦克风授权中...</p>
        </template>
        <template v-else>
          <p class="text-colorTextSecondary py-[1px] text-sm">
            请对准麦克风说话
          </p>
          <p class="text-colorText mt-2 text-xl font-bold">
            {{ secondsToStart }}s后开始录制
          </p>
        </template>
      </div>
      <div v-else-if="step === 'RECORDING'" class="flex-1 text-center">
        <p class="text-colorTextSecondary py-[1px] text-sm">
          {{ recordingText }}
        </p>
        <RecordLoading class="mt-2" />
      </div>

      <AudioMessage
        v-else-if="step === 'RECORD_SUCCESS'"
        :duration="audioInfo.duration"
        :url="audioInfo.url"
      />
    </div>
    <div class="flex h-[32px] items-center gap-3">
      <a-button :disabled="false" class="border-button mr-auto" @click="close">
        取消
      </a-button>

      <a-button
        :disabled="false"
        class="border-button"
        @click="startCountdownBeforeStart"
      >
        <img
          class="mr-1 w-[14px]"
          src="@core/assets/image/im/icon-reset.svg"
          alt=""
        />
        重录
      </a-button>
      <a-button
        v-if="step === 'RECORDING'"
        :disabled="false"
        class="flex-center !flex !h-[32px] min-w-[110px]"
        type="primary"
        @click="handleStop"
      >
        完成录音
      </a-button>
      <a-button
        v-else-if="step === 'RECORD_SUCCESS'"
        :disabled="false"
        class="flex-center !flex !h-[32px] min-w-[110px]"
        type="primary"
        @click="sendAudio"
      >
        发送
      </a-button>
    </div>
  </div>
  <GeneralConfirm
    v-model:open="showPermissionModal"
    :title="'发送语音请先开启录音权限'"
    :auto-close="true"
    :ok-text="'我知道了'"
    @ok="showPermissionModal = false"
  />
</template>
<style scoped>
.border-button {
  @apply flex-center !flex !h-[32px] min-w-[110px] hover:!border-inherit hover:!text-inherit;
}
</style>
